import React, { useState, useEffect } from "react";

/**
 * Vendr-inspired animated savings section with counter animation
 */
const VendrSavingsSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [animatedValue, setAnimatedValue] = useState(0);
  const [studentsValue, setStudentsValue] = useState(0);
  const [universitiesValue, setUniversitiesValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const targetValue = 2400; // $2,400
  const studentsTarget = 1200;
  const universitiesTarget = 20;
  const duration = 2500; // 2.5 seconds

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Intersection Observer to trigger animation when section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isAnimating && !isComplete) {
            setIsVisible(true);
            animateCounter();
          }
        });
      },
      { threshold: 0.5 }
    );

    const section = document.getElementById("vendr-savings-section");
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, [isAnimating, isComplete]);

  const animateCounter = () => {
    setIsAnimating(true);
    const startTime = Date.now();

    const updateCounters = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Smoother easing function for more natural counting
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);

      // Animate all three counters
      const currentSavings = Math.floor(targetValue * easeOutQuart);
      const currentStudents = Math.floor(studentsTarget * easeOutQuart);
      const currentUniversities = Math.floor(universitiesTarget * easeOutQuart);

      setAnimatedValue(currentSavings);
      setStudentsValue(currentStudents);
      setUniversitiesValue(currentUniversities);

      if (progress < 1) {
        requestAnimationFrame(updateCounters);
      } else {
        // Ensure we're exactly at the target values
        setAnimatedValue(targetValue);
        setStudentsValue(studentsTarget);
        setUniversitiesValue(universitiesTarget);
        setIsComplete(true);
        setTimeout(() => {
          setIsAnimating(false);
        }, 200);
      }
    };

    requestAnimationFrame(updateCounters);
  };

  const formatNumber = (num) => {
    return num.toString();
  };

  // Create fixed 4-digit layout
  const getDigits = (num) => {
    const formatted = formatNumber(num);
    const digits = formatted.split("");
    // Pad with "0" to always have 4 digits
    while (digits.length < 4) {
      digits.unshift("0");
    }
    return digits;
  };

  return (
    <div
      id="vendr-savings-section"
      className="vendr-savings-section"
    >
      <div className="vendr-savings-container">
        {/* Compact, integrated stats row */}
        <div className="savings-stats-row">
          <div className="stat-item">
            <div className={`stat-value ${isVisible ? "animate-in" : ""}`}>
              {studentsValue.toLocaleString()}+
            </div>
            <div className="stat-label">Students</div>
          </div>

          <div className="stat-divider"></div>

          <div className="stat-item highlight">
            <div className={`stat-value primary ${isVisible ? "animate-in" : ""} ${isComplete ? "bounce-complete" : ""}`}>
              ${animatedValue.toLocaleString()}+
            </div>
            <div className="stat-label primary">Saved Annually</div>
          </div>

          <div className="stat-divider"></div>

          <div className="stat-item">
            <div className={`stat-value ${isVisible ? "animate-in" : ""}`}>
              {universitiesValue}+
            </div>
            <div className="stat-label">Universities</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendrSavingsSection;
